import { ReactElement, useEffect, useRef, useState } from "react";
import {
  completeRedirectLogin,
  hasInitiatedRedirectLogin,
  initiateRedirectLogin,
} from "@technologyfromsage/persona-padlock";
import { getPersonaConfig } from "shared/AuthenticatedPage/config";
import { REDIRECT_AUTH_STATE } from "shared/localStorage/keys";
import { useAppDispatch } from "shared/redux/hooks";
import { setCurrentUser } from "shared/redux/currentUser";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ErrorPage from "shared/ErrorPage";

export type RedirectAuthStorage = {
  authProvider: string;
  redirectUri: string;
};

const getRedirectAuthState = (): RedirectAuthStorage | null => {
  const state = localStorage.getItem(REDIRECT_AUTH_STATE);
  if (!state) {
    return null;
  }

  return JSON.parse(state);
};

const RedirectAuthPage = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticating = useRef(false);
  const { t } = useTranslation();

  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    const login = async () => {
      isAuthenticating.current = true;

      const redirectAuthState = getRedirectAuthState();
      if (!redirectAuthState) {
        navigate("/");
        return;
      }

      const personaConfig = getPersonaConfig(redirectAuthState.authProvider);

      if (hasInitiatedRedirectLogin()) {
        try {
          const loginResponse = await completeRedirectLogin(personaConfig);
          dispatch(setCurrentUser(loginResponse));
          navigate(redirectAuthState.redirectUri);
        } catch {
          setError(
            Error(
              t(
                "redirectAuthPage.completeRedirectLogin-error",
                "An error occurred while logging you in.",
              ),
            ),
          );
        } finally {
          localStorage.removeItem(REDIRECT_AUTH_STATE);
        }
      } else {
        try {
          await initiateRedirectLogin(personaConfig);
        } catch {
          setError(
            Error(
              t(
                "redirectAuthPage.initiateRedirectLogin-error",
                "An error occurred while starting the login process.",
              ),
            ),
          );
        }
      }
    };

    if (isAuthenticating.current === false) {
      login();
    }
  }, [dispatch, navigate, t]);

  if (error) {
    return <ErrorPage error={error} />;
  }

  return <></>;
};

export default RedirectAuthPage;
